import {
  BUTTON_VALUE_THRESHOLD,
  DEFAULT_INPUT_MAPPING,
  type InputMapping,
  type R2Action,
} from './InputMapping';
import { createInputSnapshot, type InputSnapshot } from './InputSnapshot';

/**
 * Keyboard + gamepad for the R2 path, as one snapshot per rAF tick.
 *
 * It owns no game state and no React: `sample()` merges the held keys with pad
 * buttons and axes, resolves the edges against the previous tick, and hands
 * back the same snapshot object every time — the caller reads it within the
 * tick and must not retain it.
 */
const ACTIONS: readonly R2Action[] = ['up', 'down', 'left', 'right', 'fire'];

/** Typing into the page's own controls must not also fly the ship. */
function isNativeUiControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.closest('button, input, select, textarea, a, [role="button"], [contenteditable="true"]') !== null;
}

function noneHeld(): Record<R2Action, boolean> {
  return { up: false, down: false, left: false, right: false, fire: false };
}

export class R2InputController {
  private readonly mapping: InputMapping;
  private readonly keyboardHeld = noneHeld();
  private readonly padHeld = noneHeld();
  private readonly snapshot = createInputSnapshot();
  private padAxisX = 0;
  private padAxisY = 0;
  private padActive = false;
  private previousFire = false;
  private lastSource: 'keyboard' | 'gamepad' = 'keyboard';
  private target: Window | null = null;

  constructor(mapping: InputMapping = DEFAULT_INPUT_MAPPING) {
    this.mapping = mapping;
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (isNativeUiControl(event.target)) return;
    const action = this.mapping.keyboard[event.code];
    if (action === undefined) return;
    event.preventDefault();
    this.keyboardHeld[action] = true;
    this.lastSource = 'keyboard';
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (isNativeUiControl(event.target)) return;
    const action = this.mapping.keyboard[event.code];
    if (action === undefined) return;
    event.preventDefault();
    this.keyboardHeld[action] = false;
    this.lastSource = 'keyboard';
  };

  /** A key held across a focus loss never reports its keyup — drop everything. */
  private readonly onBlur = (): void => {
    for (const action of ACTIONS) this.keyboardHeld[action] = false;
  };

  attach(target: Window): void {
    if (this.target === target) return;
    this.dispose();
    this.target = target;
    target.addEventListener('keydown', this.onKeyDown, { passive: false });
    target.addEventListener('keyup', this.onKeyUp, { passive: false });
    target.addEventListener('blur', this.onBlur);
  }

  sample(): InputSnapshot {
    this.pollGamepad();
    let x = (this.isHeld('right') ? 1 : 0) - (this.isHeld('left') ? 1 : 0);
    let y = (this.isHeld('down') ? 1 : 0) - (this.isHeld('up') ? 1 : 0);
    if (Math.abs(this.padAxisX) > Math.abs(x)) x = this.padAxisX;
    if (Math.abs(this.padAxisY) > Math.abs(y)) y = this.padAxisY;
    const lenSq = x * x + y * y;
    if (lenSq > 1) {
      const inv = 1 / Math.sqrt(lenSq);
      x *= inv;
      y *= inv;
    }
    const fire = this.isHeld('fire');
    if (!this.padActive && this.keyboardActive()) this.lastSource = 'keyboard';
    const snapshot = this.snapshot;
    snapshot.moveX = x;
    snapshot.moveY = y;
    snapshot.fire = fire;
    snapshot.firePressed = fire && !this.previousFire;
    snapshot.fireReleased = !fire && this.previousFire;
    snapshot.source = this.lastSource;
    this.previousFire = fire;
    return snapshot;
  }

  dispose(): void {
    const target = this.target;
    if (!target) return;
    target.removeEventListener('keydown', this.onKeyDown);
    target.removeEventListener('keyup', this.onKeyUp);
    target.removeEventListener('blur', this.onBlur);
    this.target = null;
  }

  private isHeld(action: R2Action): boolean {
    return this.keyboardHeld[action] || this.padHeld[action];
  }

  private keyboardActive(): boolean {
    for (const action of ACTIONS) {
      if (this.keyboardHeld[action]) return true;
    }
    return false;
  }

  /** Pad 0 only, standard mapping; a missing API or no pad leaves the keyboard as the whole input. */
  private pollGamepad(): void {
    for (const action of ACTIONS) this.padHeld[action] = false;
    this.padAxisX = 0;
    this.padAxisY = 0;
    this.padActive = false;
    const getGamepads = navigator.getGamepads;
    if (typeof getGamepads !== 'function') return;
    const pad = getGamepads.call(navigator)[0];
    if (!pad) return;
    for (const action of ACTIONS) {
      const indices = this.mapping.gamepadButtons[action];
      if (indices === undefined) continue;
      for (const index of indices) {
        const button = pad.buttons[index];
        // Analog triggers report a value and may never set `pressed`.
        if (!button || (!button.pressed && button.value <= BUTTON_VALUE_THRESHOLD)) continue;
        this.padHeld[action] = true;
        this.padActive = true;
      }
    }
    const axes = this.mapping.gamepadAxes;
    const rawX = pad.axes[axes.moveX] ?? 0;
    const rawY = pad.axes[axes.moveY] ?? 0;
    if (Math.abs(rawX) > axes.deadZone) this.padAxisX = rawX;
    if (Math.abs(rawY) > axes.deadZone) this.padAxisY = rawY;
    if (this.padAxisX !== 0 || this.padAxisY !== 0) this.padActive = true;
    if (this.padActive) this.lastSource = 'gamepad';
  }
}
